import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * The single source of truth for "who am I, and may I see the admin area".
 *
 * One shared TanStack Query entry, not per-component state. Every consumer
 * (AuthGuard, Layout, AdminGate, UserMenu) reads the same cache entry,
 * so they all see the *same* snapshot in the *same* render. That consistency
 * is the whole point: the admin gate compares "is the session resolved"
 * against "does it carry an admin role", and per-component fetches answer
 * those two questions from two different requests. One of them resolving
 * first, while the other still holds an empty role list, reads as a resolved
 * non-admin — and bounces the operator to the dashboard.
 *
 * The other half of the contract is that `roles` is never guessed. A request
 * that failed (backend restarting mid-deploy, a 502 from the proxy, a cold DB
 * pool) leaves the query pending or erroring — it never presents itself as an
 * authoritative empty role list. Callers see `isAdmin === undefined` and must
 * wait rather than act.
 */

export type AuthStatus =
  | "loading" // no answer yet (including while retrying a transient failure)
  | "authenticated" // session resolved, `roles` is authoritative
  | "unauthenticated" // the server said 401 — no valid session
  | "unavailable"; // a usable answer never arrived (deploy, outage)

export interface AuthState {
  username: string | null;
  email: string | null;
  roles: string[];
  status: AuthStatus;
  /** True while no answer has arrived yet. */
  loading: boolean;
  /**
   * `true` / `false` once `roles` is authoritative, `undefined` while it is
   * not. Never collapse `undefined` to `false` in a gate — that is exactly
   * the bug this tri-state exists to prevent.
   */
  isAdmin: boolean | undefined;
  /** Re-run the session lookup (drives the "unavailable" retry button). */
  refresh: () => void;
}

interface MeResponse {
  username: string | null;
  email: string | null;
  roles: string[];
}

const AUTH_QUERY_KEY = ["auth", "me"] as const;

/** Thrown only when the server explicitly denies the session (HTTP 401). */
class UnauthenticatedError extends Error {
  constructor() {
    super("not authenticated");
    this.name = "UnauthenticatedError";
  }
}

async function fetchMeOnce(): Promise<MeResponse> {
  const res = await fetch("/api/auth/me", {
    credentials: "include",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (res.status === 401) throw new UnauthenticatedError();
  if (!res.ok) throw new Error(`/api/auth/me failed: ${res.status}`);
  const data = (await res.json()) as Partial<MeResponse>;
  return {
    username: data.username ?? null,
    email: data.email ?? null,
    // The backend answers 503 rather than an empty list when it cannot read
    // roles, so an empty array here means "this user genuinely has none".
    roles: Array.isArray(data.roles) ? data.roles : [],
  };
}

/** Backoff for the in-fetch retries, in ms. Four attempts, ~3.5s worst case. */
const RETRY_DELAYS_MS = [400, 1_000, 2_000];

/**
 * Resolve the session, riding out a restarting backend inside a single query
 * attempt.
 *
 * The retries live *here* rather than being left to TanStack Query's `retry`
 * on purpose. Query's retry scheduler pauses while the document is unfocused
 * or hidden (`retryer.ts`: a paused retry waits for `focusManager.isFocused()`),
 * which is right for a data poll and wrong for this one query: the whole app
 * chrome waits on it, so a tab that loaded in the background — a restored
 * session, a cmd-clicked link, a deploy the operator switched away from —
 * would sit on "Loading…" until it was looked at. Query's own `retry` stays on
 * as an outer net for whatever gets past these.
 */
async function fetchMe(): Promise<MeResponse> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetchMeOnce();
    } catch (err) {
      // A 401 is a verdict, not a hiccup — surface it immediately.
      if (err instanceof UnauthenticatedError) throw err;
      if (attempt >= RETRY_DELAYS_MS.length) throw err;
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
    }
  }
}

/** Any role whose name contains "admin" (e.g. `crm-admin`) grants the area. */
function hasAdminRole(roles: string[]): boolean {
  return roles.some((r) => r.toLowerCase().includes("admin"));
}

export function useAuth(): AuthState {
  const query = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: fetchMe,
    // Identity is stable for the life of a page; navigating between routes
    // must not re-open the question.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    // `fetchMe` already rode out the transient window; a 401 is a verdict and
    // retrying it only delays the sign-in card. This is the outer net.
    retry: (attempt, error) =>
      !(error instanceof UnauthenticatedError) && attempt < 2,
    retryDelay: 3_000,
  });

  const unauthenticated = query.error instanceof UnauthenticatedError;
  const data = query.data;

  let status: AuthStatus;
  if (unauthenticated) {
    status = "unauthenticated";
  } else if (data) {
    // A transient failure while refetching keeps the last good answer — the
    // app stays up instead of tearing itself down over one bad response.
    status = "authenticated";
  } else if (query.isPending) {
    status = "loading";
  } else {
    status = "unavailable";
  }

  return {
    username: data?.username ?? null,
    email: data?.email ?? null,
    roles: data?.roles ?? [],
    status,
    loading: status === "loading",
    isAdmin: status === "authenticated" ? hasAdminRole(data!.roles) : undefined,
    refresh: () => {
      void query.refetch();
    },
  };
}

/**
 * Admin predicate. `undefined` means "not known yet" — render nothing, don't
 * redirect. Only `false` is a denial.
 */
export function useIsAdmin(): boolean | undefined {
  return useAuth().isAdmin;
}

/**
 * Re-verifies the session when the API client reports a 401.
 *
 * A 401 from one data endpoint is a claim, not a verdict. Acting on it
 * directly tears down the whole app — AuthGuard swaps `children` for the
 * sign-in card — taking any open drawer and half-written text with it. A
 * backgrounded tab resumes several polls at once on return, which is exactly
 * when one unlucky response is most likely. So re-ask `/api/auth/me` and let
 * its answer decide. Mounted once, by AuthGuard.
 */
export function useAuthExpiryWatcher(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const onExpired = () => {
      void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, [queryClient]);
}

/**
 * End the WebUI session: the server clears the cookie, then a full page load
 * resets every cached query. A failed request rejects — the caller shows it.
 */
export async function signOut(): Promise<void> {
  const res = await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "include",
    headers: { "X-Requested-With": "webui" },
  });
  if (!res.ok) throw new Error(`Sign-out failed (${res.status})`);
  window.location.assign("/");
}
